import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import { Controls } from '@/main/services/constants/constantsControl';
import {
  getTranslateByKey,
  isNullUndefined,
  isShowControlByElementId,
  removeEmptyValues
} from '@/main/services/utils';
import { userIsSupplier } from '@/main/services/utilsUser';
import { useEffect, useState } from 'react';

import { quoteApi } from '@/main/api';
import useAsyncModalConfirm from '@/main/components/shared/asyncModalConfirm/asyncModalConfirm';
import AsyncModalConfirmWithLoading from '@/main/components/shared/asyncModalConfirmWithLoading/asyncModalConfirmWithLoading';
import Button from '@/main/components/shared/button/button';
import { QuoteAwaitingDetailResponse } from '@/main/models/response';
import { RoutesConstants } from '@/main/routes/routesConstants';
import { AutomaticActionEnum } from '@/main/services/enum/AutomaticActionEnum';
import { AwaitingReleaseResonEnum } from '@/main/services/enum/AwaitingReleaseResonEnum';
import { ProcurementStatusEnum } from '@/main/services/enum/StatusProcurement';
import {
  getCheckoutSuccessMessage,
  isCheckoutAwaitingApproval
} from '@/main/services/utilsCheckout';
import { getRefreshForceUrl } from '@/main/services/utilsQueryParams';
import {
  resetReducerByIdAction,
  useAutomaticActionReducer
} from '@/main/store/automaticAction';
import { CartState } from '@/main/store/cart';
import { isNil } from 'lodash';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as S from './styles';

interface IFooterCollapseQuoteProps {
  guidQuote: string;
  dataQuote: QuoteAwaitingDetailResponse;
  dataCart: CartState;
  canShowDeleteButton: boolean;
  getCart: () => Promise<void>;
  getDetailsQuote: (
    guidQuoteDetails: string,
    showLoading?: boolean
  ) => Promise<void>;
  refreshGrid?: () => void;
}

function FooterCollapseQuote({
  guidQuote,
  dataQuote,
  dataCart,
  canShowDeleteButton,
  getCart,
  getDetailsQuote,
  refreshGrid = () => { }
}: IFooterCollapseQuoteProps) {
  const [isStripe, setIsStripe] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { automaticAction } = useAutomaticActionReducer();

  const cartReducer = dataCart;
  const [getConfirmation, ModalConfirmation] = useAsyncModalConfirm();
  const [isOpenModalReopenOffer, setIsOpenModalReopenOffer] =
    useState<boolean>(false);

  const cartNoSupplierAssigned = cartReducer.cartItems.dataNoSupplierAssigned;
  const cartDataUnansweredItems =
    cartReducer.cartItems.dataUnansweredItems?.parts;

  const showReopenOfferButton = isShowControlByElementId(
    Controls.IdReopenOffer
  );

  const [isLoadingGenerateOrder, setIsLoadingGenerateOrder] =
    useState<boolean>(false);
  const isWaitingRelease =
    dataQuote.idProcurementStatus === ProcurementStatusEnum.WaitingRelease;
  const hasWaitinReleasePermission = isShowControlByElementId(
    Controls.IdReleaseOrderControl
  );
  const hideReleaseActionForSupplier = userIsSupplier() && isWaitingRelease;
  const isIntegration = dataQuote.integration;
  const isQuoteOpen =
    dataQuote.idProcurementStatus === ProcurementStatusEnum.Open;

  const showSummaryButton = isShowControlByElementId(
    Controls.IdSearchOrderSummary
  );

  useEffect(() => {
    if (dataQuote.idProcurementStatus === ProcurementStatusEnum.Open) {
      getShowStripeButton();
    }
  }, [dataQuote]);

  useEffect(() => {
    if (!isNil(automaticAction.data) && !isLoadingGenerateOrder) {
      const findAction = automaticAction.data.find(
        x =>
          x.idAction ==
          AutomaticActionEnum.GenerateOrderBySaveBuyerPartAsnwer &&
          x.id == guidQuote
      );

      if (!isNil(findAction)) {
        dispatch(resetReducerByIdAction(guidQuote));
        handleGenerateOrderService();
      }
    }
  }, [automaticAction.data]);

  async function displayModalCancel() {
    const messageModal = getTranslateByKey(
      'information.areYouSureYouWantToCancelTheOrder'
    );
    const result = await getConfirmation(messageModal);

    if (!result) return;

    if (!guidQuote) return navigate(RoutesConstants.orderSearchOrder);
    quoteApi
      .cancelQuoteById({
        guid: guidQuote,
        route: RoutesConstants.orderSearchOrder
      })
      .then(() => {
        toast.success(
          getTranslateByKey('information.successfullyCanceledOrder')
        );
        getDetailsQuote(guidQuote, true);
      })
      .catch(() => { });
  }

  function displayModalReQuote() {
    setIsOpenModalReopenOffer(true);
  }
  async function handleRequote() {
    if (guidQuote) {
      try {
        await quoteApi.reQuote(String(guidQuote));

        await Promise.all([getDetailsQuote(guidQuote), getCart()]);
      } catch (error) {
      } finally {
        setIsOpenModalReopenOffer(false);
      }
    }
  }

  function changeQuoteToAwaitingRelease() {
    setIsLoadingGenerateOrder(true);

    quoteApi
      .changeQuoteToAwaitingRelease(String(guidQuote))
      .then(res => {
        const resData = res.data.result;
        if (resData) {
          toast.success(
            getTranslateByKey('information.quoteChangedToAwaitingRelease')
          );
          getDetailsQuote(guidQuote, true);
        }
      })
      .finally(() => {
        setIsLoadingGenerateOrder(false);
      });
  }

  async function getShowStripeButton() {
    quoteApi.showStripePaymentButton(String(guidQuote)).then(res => {
      const { result } = res.data;
      setIsStripe(result);
    });
  }

  function submitGenerateOrder() {
    const requestCheckout = removeEmptyValues({
      guidQuote: String(guidQuote),
      guidBillingAddress: String(dataQuote.guidBillingAddress)
    });

    quoteApi
      .checkout(requestCheckout)
      .then(
        res => {
          const resData = res.data;

          if (resData?.isWarning) {
            if (resData.message && !resData.isHideToastNotification) {
              toast.warn(resData.message);
            }
          } else {
            toast.success(getCheckoutSuccessMessage(resData.result));
          }

          if (isCheckoutAwaitingApproval(resData.result)) {
            navigate(
              `${RoutesConstants.orderSearchOrder}${getRefreshForceUrl({
                quoteFilterVehicle: dataQuote.claimNumber
              })}`
            );
            return;
          }

          getDetailsQuote(guidQuote, true);
        },
        error => {
          getCart();
        }
      )
      .catch(() => { })
      .finally(() => setIsLoadingGenerateOrder(false));
  }
  async function generateOrder() {
    setIsLoadingGenerateOrder(true);

    if (hasWaitinReleasePermission || !isWaitingRelease) {
      const response = await quoteApi
        .validateawaitingreleaseaprroval(String(guidQuote))
        .catch(() => null);

      if (!response) {
        setIsLoadingGenerateOrder(false);
        return;
      }

      const resData = response.data.result;
      const validateTotalLoss = {
        approved: resData.approved,
        awaitingReleaseReson: resData.awaitingReleaseReson,
        changedToAwaitingRelease: resData.changedToAwaitingRelease
      };

      if (!validateTotalLoss.changedToAwaitingRelease) {
        if (
          validateTotalLoss &&
          !validateTotalLoss.approved &&
          !isNullUndefined(validateTotalLoss.awaitingReleaseReson)
        ) {
          const MESSAGES_MODAL_AWAITING_RELEASE = {
            [AwaitingReleaseResonEnum.QuoteChangeStatusDamagePayment]:
              getTranslateByKey(
                'information.thisQuoteIsADamagePaymentAreYouSureYouWantToGenerateTheOrder'
              ),
            [AwaitingReleaseResonEnum.QuoteChangeStatusTotalLoss]:
              getTranslateByKey(
                'information.thisQuoteIsATotalLossAreYouSureYouWantToGenerateTheOrder'
              ),
            [AwaitingReleaseResonEnum.QuoteChangeStatusNotDeductible]:
              getTranslateByKey(
                'information.thisQuoteHasLessThanDeductibleRepairCostsAreYouSureYouWantToGenerateTheOrder'
              ),
            [AwaitingReleaseResonEnum.QuoteChangeStatusPossibleTotalLoss]:
              getTranslateByKey(
                'information.thatQuoteMightBeATotalLossAreYouSureThatWantToGenerateTheOrder'
              ),
            [AwaitingReleaseResonEnum.QuoteChangeStatusNotAprroved]:
              getTranslateByKey(
                'information.thisQuoteIsNotApprovedAreYouSureYouWantToGenerateTheOrder'
              )
          };

          if (isNullUndefined(validateTotalLoss.awaitingReleaseReson)) return;

          const messageModal =
            MESSAGES_MODAL_AWAITING_RELEASE[
            validateTotalLoss.awaitingReleaseReson as keyof typeof MESSAGES_MODAL_AWAITING_RELEASE
            ] ?? '';
          let resultModal = hasWaitinReleasePermission;

          if (resultModal) resultModal = await getConfirmation(messageModal);

          if (!resultModal) {
            setIsLoadingGenerateOrder(false);
            changeQuoteToAwaitingRelease();
            getDetailsQuote(guidQuote, true);
            getCart();
            refreshGrid();
            return;
          }
        }
        submitGenerateOrder();
      } else {
        toast.success(
          getTranslateByKey('information.quoteChangedToAwaitingRelease')
        );
        getDetailsQuote(guidQuote, true);
        getCart();
        setIsLoadingGenerateOrder(false);
      }
    } else {
      toast.error(
        getTranslateByKey('information.awaitingReleaseWithoutPermission')
      );
      setIsLoadingGenerateOrder(false);
    }
  }
  async function onClickSummary() {
    navigate(
      `${RoutesConstants.orderCreateOrder}?guidQuote=${guidQuote}&step=3`
    );
  }

  async function searchSupplierForParts() {
    setIsLoadingGenerateOrder(true);

    quoteApi
      .addMissingItemsToCart(String(guidQuote))
      .then(res => {
        const findSupplier = res.data.result;

        if (!findSupplier) {
          setIsLoadingGenerateOrder(false);
          toast.error(
            getTranslateByKey('information.noSuppliersWereFoundForTheParts')
          );
        } else {
          generateOrder();
        }
      })
      .catch(() => {
        setIsLoadingGenerateOrder(false);
      });
  }

  function handleStripe() {
    toast.warn(getTranslateByKey("information.pleaseNavigateToSummary"));
  }

  async function handleGenerateOrderService() {
    if (cartNoSupplierAssigned && cartNoSupplierAssigned !== null) {
      const messageModal = getTranslateByKey(
        'information.areYouThatSystemSelectBestSupplierAndCheckOutYourQuote'
      );
      const result = await getConfirmation(messageModal);

      if (!result) return;

      await searchSupplierForParts();
      return;
    }

    generateOrder();
  }

  const isDisabledGenerateOrderButton =
    isLoadingGenerateOrder ||
    (cartReducer.cartItems.data.length === 0 &&
      cartNoSupplierAssigned === null) ||
    (cartDataUnansweredItems?.length ?? 0) > 0 ||
    (isIntegration && isQuoteOpen) ||
    cartReducer.cartItems.isLoading ||
    cartReducer.cartItems.isLoadingUpdate;

  const ID_SCREEN = 'id-infoOrder';
  const generateId = (id: string) => `${ID_SCREEN}-${id}`;
  return (
    <S.Container>
      {canShowDeleteButton && !hideReleaseActionForSupplier && (
        <SecondaryButton
          id={generateId('button-cancel')}
          type="button"
          onClick={() => displayModalCancel()}>
          {getTranslateByKey('information.cancel')}
        </SecondaryButton>
      )}
      {showReopenOfferButton && (
        <SecondaryButton
          id={generateId('button-reopenOffer')}
          type="button"
          onClick={() => displayModalReQuote()}>
          {getTranslateByKey('information.reopenOffer')}
        </SecondaryButton>
      )}

      {showSummaryButton && (
        <SecondaryButton
          id={generateId('button-summary')}
          type="button"
          onClick={() => onClickSummary()}>
          {getTranslateByKey('information.summary')}
        </SecondaryButton>
      )}

      {!hideReleaseActionForSupplier && (
        <Button
          id={generateId('button-generateOrder')}
          type="button"
          isLoadingLockScreen
          isLoading={isLoadingGenerateOrder}
          disabled={isDisabledGenerateOrderButton}
          onClick={() =>
            isStripe ? handleStripe() : handleGenerateOrderService()
          }>
          {isStripe
            ? getTranslateByKey('information.buyNow')
            : getTranslateByKey('information.generateOrder')}
        </Button>
      )}

      <ModalConfirmation />
      <AsyncModalConfirmWithLoading
        id="id-modal-reopen-offer"
        isOpen={isOpenModalReopenOffer}
        setIsOpen={setIsOpenModalReopenOffer}
        label={`${getTranslateByKey('information.areYouSureYouWantToReopenOffer')}?`}
        onConfirm={handleRequote}
        onCancel={() => setIsOpenModalReopenOffer(false)}
      />
    </S.Container>
  );
}

export default FooterCollapseQuote;
